import { useCallback, useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import {
  getNativePushPermissionState,
  requestNativePushPermissionAndRegister,
} from "@/lib/native/pushNotifications";
import {
  markNativePushTipDismissed,
  NATIVE_PUSH_TIP_DISMISSED_EVENT,
  wasNativePushTipDismissed,
} from "@/lib/native/nativePushTipStorage";
import { cn } from "@/lib/utils";

/**
 * Compact dashboard tip when OS push permission is not granted yet.
 * Complements NativePushOptInDialog for users who dismiss the sheet.
 */
export function NativePushTipBanner() {
  const { isNative } = useNativePlatform();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { language } = useLanguage();
  const labels = dashboardLabels(language)?.pushTip;
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNative || !user?.id || !labels) {
      setVisible(false);
      return;
    }
    if (wasNativePushTipDismissed(user.id)) {
      setVisible(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      const state = await getNativePushPermissionState();
      if (cancelled) return;
      if (state === "granted" || state === "unavailable") {
        if (state === "granted") markNativePushTipDismissed(user.id);
        setVisible(false);
        return;
      }
      setVisible(true);
    })();

    const onExternalDismiss = () => setVisible(false);
    window.addEventListener(NATIVE_PUSH_TIP_DISMISSED_EVENT, onExternalDismiss);

    return () => {
      cancelled = true;
      window.removeEventListener(
        NATIVE_PUSH_TIP_DISMISSED_EVENT,
        onExternalDismiss,
      );
    };
  }, [isNative, user?.id, labels]);

  const dismiss = useCallback(() => {
    if (user?.id) markNativePushTipDismissed(user.id);
    setVisible(false);
  }, [user?.id]);

  const activate = useCallback(async () => {
    setBusy(true);
    try {
      const result = await requestNativePushPermissionAndRegister();
      if (result.granted && user?.id) {
        markNativePushTipDismissed(user.id);
      }
      setVisible(false);
    } finally {
      setBusy(false);
    }
  }, [user?.id]);

  if (!visible || !labels) return null;

  return (
    <section
      className={cn(
        "mb-4 overflow-hidden rounded-2xl border border-sky-500/25 bg-gradient-to-br from-sky-500/[0.07] via-background to-emerald-500/[0.05] native-dashboard-banner",
        isNative ? "px-4 py-3.5" : "px-3 py-2.5 sm:px-4 sm:py-3",
        isMobile ? "w-full" : "w-[85%] mx-auto",
      )}
      role="status"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-sky-500/12 text-sky-700 dark:text-sky-300">
          <Bell className="h-4 w-4" strokeWidth={2.25} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold tracking-tight text-foreground">
            {labels.title}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">
            {labels.description}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => void activate()}
            >
              {busy ? labels.activating : labels.cta}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={dismiss}
              className="text-muted-foreground"
            >
              {labels.later}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          aria-label={labels.dismissAria}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
}
